import React from "react";
import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
import { Formik } from "formik";
import { Box, Stack, TextField, Typography } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";

import useAuth from "../../../hooks/useAuth";
import { Alert } from "../../../components/pages/common/styled";
import { getUserRoleString } from "../../../utils/format";

import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import {
  TOKENS,
  SectionCard,
  PlanChip,
  Monogram,
} from "../../../components/pages/_new/super/design";

function SAProfile() {
  const { updateProfile, adminUserType, user } = useAuth();

  const roleStr = getUserRoleString(adminUserType) || "Admin";
  const userName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email || roleStr;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${TOKENS.radiusXs}px`,
      background: TOKENS.bgSurface,
    },
  };
  const labelSx = { fontSize: 12.5, color: TOKENS.fg2, mb: 0.75, fontWeight: 500 };

  return (
    <React.Fragment>
      <Helmet title="SA Profile" />
      <PageHeader title={`${roleStr} Profile`} description="Account details, password, and session management" />

      <Stack spacing={2}>
        <SectionCard title="Account" description="Your basic identity">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <Monogram name={userName} size={56} radius={14} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: TOKENS.fg1 }}>{userName}</Typography>
              <Typography sx={{ fontSize: 13, color: TOKENS.fg2, mt: 0.25 }}>{user?.email || "—"}</Typography>
              <Box sx={{ mt: 1 }}>
                <PlanChip plan={roleStr} mini />
              </Box>
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard title="Password" description="Choose a new password with at least 8 characters">
          <Formik
            initialValues={{ password: "", confirmPassword: "" }}
            validationSchema={Yup.object().shape({
              password: Yup.string().min(8, "Must be at least 8 characters").max(255).required("Required"),
              confirmPassword: Yup.string().when("password", {
                is: (val) => (val && val.length > 0 ? true : false),
                then: Yup.string().oneOf([Yup.ref("password")], "Both password need to be the same"),
              }),
            })}
            onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
              try {
                const response = await updateProfile(values);
                if (response) {
                  resetForm();
                  setErrors({ success: "Password has been changed successfully." });
                }
              } catch (error) {
                const message = error.message || "Something went wrong";
                setStatus({ success: false });
                setErrors({ submit: message });
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box sx={{ maxWidth: 560 }}>
                  {errors.submit && (
                    <Alert mt={2} mb={3} variant="outlined" severity="error">
                      {errors.submit}
                    </Alert>
                  )}
                  {errors.success && (
                    <Alert mt={2} mb={3} variant="outlined" severity="info">
                      {errors.success}
                    </Alert>
                  )}
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography sx={labelSx}>New password</Typography>
                      <TextField
                        size="small"
                        type="password"
                        name="password"
                        value={values.password}
                        error={Boolean(touched.password && errors.password)}
                        fullWidth
                        helperText={touched.password && errors.password}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        sx={inputSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={labelSx}>Confirm password</Typography>
                      <TextField
                        size="small"
                        type="password"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                        fullWidth
                        helperText={touched.confirmPassword && errors.confirmPassword}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        sx={inputSx}
                      />
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <PrimaryButton type="submit" disabled={isSubmitting} startIcon={<EditIcon style={{ width: 18, height: 18 }} />}>
                        Change Password
                      </PrimaryButton>
                    </Box>
                  </Stack>
                </Box>
              </form>
            )}
          </Formik>
        </SectionCard>
      </Stack>
    </React.Fragment>
  );
}
export default SAProfile;
